import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { fetchRuntimeConfig } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { canSee, navItemFor } from '@/lib/nav';
import { Dashboard } from '@/pages/Dashboard';
import { Accounts } from '@/pages/Accounts';
import { Login } from '@/pages/Login';
import { ItemGroups } from '@/pages/ItemGroups';
import { Items } from '@/pages/Items';
import { Kopie } from '@/pages/Kopie';
import { Logs } from '@/pages/Logs';
import { Machines } from '@/pages/Machines';
import PulpitOkno from '@/pages/PulpitOkno';
import { Permissions } from '@/pages/Permissions';
import { Planogram } from '@/pages/Planogram';
import { Settings } from '@/pages/Settings';
import { Assignments } from '@/pages/Assignments';
import { Authoring } from '@/pages/Authoring';
import { Agents } from '@/pages/Agents';
import { SmartSync } from './pages/SmartSync';
import { Programy } from '@/pages/Programy';
import { Alerts } from '@/pages/Alerts';
import { Changes } from '@/pages/Changes';
import { CostCenters } from '@/pages/CostCenters';
import { EmployeeCard } from '@/pages/EmployeeCard';
import { MachineCard } from '@/pages/MachineCard';
import { EmployeeGroups } from '@/pages/EmployeeGroups';
import { Employees } from '@/pages/Employees';
import { Harmonogramy } from '@/pages/Harmonogramy';
import { Raportowanie } from '@/pages/Raportowanie';
import { RaportySps } from '@/pages/RaportySps';
import { Transactions } from '@/pages/Transactions';
import { Nagrania } from '@/pages/Nagrania';
import { HrPlus } from '@/pages/HrPlus';

export function App() {
  const { data: cfg } = useQuery({ queryKey: ['runtime-config'], queryFn: fetchRuntimeConfig });
  const { user, isLoading } = useAuth();
  const { t } = useI18n();

  // dopóki nie wiemy czy jest sesja, nie migamy ekranem logowania
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 bg-background text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        <span className="text-sm">{t('common.checkingSession')}</span>
      </div>
    );
  }

  if (!user) {
    return <Login authMode={cfg?.auth.mode} version={cfg?.app.version} />;
  }

  /**
   * BRAMKA TRASY — ta sama reguła, którą liczy menu (`lib/nav.ts`).
   *
   * Do 2026-08-03 trasy nie sprawdzały niczego: menu ukrywało pozycję, a wpisanie adresu
   * z palca renderowało ekran mimo wszystko. Przy koncie klienta to przestaje być kosmetyką —
   * `/edytor` to najgroźniejsza powierzchnia w portalu.
   *
   * Ekran „brak dostępu" zamiast przekierowania na pulpit: ciche przeniesienie wygląda jak
   * usterka nawigacji, a odmowa mówi, co się stało.
   */
  const Guarded = ({ path, element }: { path: string; element: ReactNode }) => {
    const item = navItemFor(path);
    // adres poza nawigacją traktujemy jako niedostępny — fail-closed, nie „przepuść bo nie wiem"
    if (!item || !canSee(user, item)) {
      return (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <ShieldOff className="mx-auto size-8 text-muted-foreground/60" />
          <p className="mt-3 text-sm font-medium">{t('access.denied')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('access.deniedHint')}</p>
        </div>
      );
    }
    return element;
  };

  return (
    <Routes>
      {/*
        ══════════════════════════════════════════════════════════════════════════════════════════
         PULPIT MASZYNY STOI POZA POWŁOKĄ — bez menu, nagłówka i wyboru wyspy.

         ⚠️⚠️ ZGŁOSZONE 15.09.2026: „przejście na inną stronę panelu je zupełnie wyłącza — raczej
         to powinno być w jakimś osobnym okienku". Otwieramy go w OSOBNYM OKNIE przeglądarki,
         a wtedy powłoka portalu jest tam wyłącznie zabraną powierzchnią: to okno ma jedno zadanie
         i cała jego szerokość należy do obrazu maszyny.

         ⚠️⚠️ ADRES JEST DZIECKIEM `/automaty`, I TO NIE JEST KOSMETYKA URL-a. `ui.trasy.spec.ts`
         pilnuje, żeby trasa strzeżona cudzym adresem BYŁA jego trasą podrzędną — inaczej dowolny
         ekran mógłby pożyczyć sobie uprawnienie, którego nie dotyczy. Pierwsza wersja stała pod
         `/pulpit/:machineId` i strażnik słusznie ją odrzucił.

         ⚠️ RESZTA WCHODZI PRZEZ TRASĘ UKŁADU (`AppShell` z `Outlet`), a nie przez powłokę owiniętą
         wokół `Routes`. Zagnieżdżenie tras w `element={…}` rozsypuje strażnika z `ui.trasy.spec.ts`,
         który czyta pary (trasa, adres `Guarded`) wprost ze źródła — i słusznie, bo to on pilnuje,
         żeby żadna trasa nie została bez zadeklarowanych uprawnień.
        ══════════════════════════════════════════════════════════════════════════════════════════
      */}
      <Route path="/automaty/:machineId/pulpit" element={<Guarded path="/automaty" element={<PulpitOkno />} />} />
      <Route element={<AppShell version={cfg?.app.version} />}>
        {/*
          Trasy i menu czytają JEDNĄ tabelę (`NAV`) — pozycja bez wpisu w tabeli jest
          niedostępna, więc dodanie ekranu bez uprawnienia nie przechodzi po cichu.
        */}
        <Route path="/" element={<Guarded path="/" element={<Dashboard />} />} />
        <Route path="/automaty" element={<Guarded path="/automaty" element={<Machines />} />} />
        {/*
          ⚠️ USTAWIENIA WYSPY SĄ ZAKŁADKĄ AUTOMATÓW (23.09.2026), a ten adres żył jako osobny ekran
          kilka godzin. Bez przekierowania trafiałby w kartę automatu o identyfikatorze „ustawienia".
        */}
        <Route path="/automaty/ustawienia" element={<Guarded path="/automaty" element={<Navigate to="/automaty?zakladka=ustawienia" replace />} />} />
        {/**
          * KARTA AUTOMATU — trasa PODRZĘDNA automatów, więc `Guarded` dostaje adres RODZICA.
          * Ta sama zasada, co przy karcie pracownika: uprawnienie dotyczy ekranu automatów,
          * a nie osobnego adresu, którego nikt nie nadaje w rolach.
          */}
        <Route path="/automaty/:id" element={<Guarded path="/automaty" element={<MachineCard />} />} />
        <Route path="/produkty" element={<Guarded path="/produkty" element={<Items />} />} />
        <Route path="/grupy-towarowe" element={<Guarded path="/grupy-towarowe" element={<ItemGroups />} />} />
        <Route path="/planogram" element={<Guarded path="/planogram" element={<Planogram />} />} />
        <Route path="/transakcje" element={<Guarded path="/transakcje" element={<Transactions />} />} />
        <Route path="/nagrania" element={<Guarded path="/nagrania" element={<Nagrania />} />} />
        <Route path="/hr-plus" element={<Guarded path="/hr-plus" element={<HrPlus />} />} />
        <Route path="/raportowanie" element={<Guarded path="/raportowanie" element={<Raportowanie />} />} />
        {/*
          HARMONOGRAMY WYSYŁKI — trasa PODRZĘDNA raportów, więc `Guarded` dostaje adres rodzica.
          Wymagania dostępu są dokładnie te same (moduł `reports`) i mają z niego POCHODZIĆ:
          własny wpis w `NAV` znaczyłby drugą listę uprawnień do utrzymania, a harmonogram jest
          sposobem dostania tego samego raportu bez klikania.
        */}
        <Route
          path="/raportowanie/harmonogramy"
          element={<Guarded path="/raportowanie" element={<Harmonogramy />} />}
        />
        {/* Raporty skonfigurowane w SPS (13.10.2026) — ten sam dostęp co raporty, tak jak harmonogramy. */}
        <Route path="/raportowanie/sps" element={<Guarded path="/raportowanie" element={<RaportySps />} />} />
        <Route path="/pracownicy" element={<Guarded path="/pracownicy" element={<Employees />} />} />
        {/*
          KARTA POJEDYNCZEGO PRACOWNIKA — trasa podrzędna, więc `Guarded` dostaje adres RODZICA.
          Wymagania dostępu są dokładnie te same co dla listy (moduł `employees`) i mają z niej
          POCHODZIĆ, a nie być zadeklarowane drugi raz: własny wpis w `NAV` znaczyłby dwie listy
          uprawnień do tego samego ekranu i pozycję w menu, której nie da się kliknąć bez id.
        */}
        <Route path="/pracownicy/:id" element={<Guarded path="/pracownicy" element={<EmployeeCard />} />} />
        <Route path="/grupy" element={<Guarded path="/grupy" element={<EmployeeGroups />} />} />
        <Route path="/przypisania" element={<Guarded path="/przypisania" element={<Assignments />} />} />
        <Route path="/centra-kosztowe" element={<Guarded path="/centra-kosztowe" element={<CostCenters />} />} />
        <Route path="/uprawnienia" element={<Guarded path="/uprawnienia" element={<Permissions />} />} />
        <Route path="/edytor" element={<Guarded path="/edytor" element={<Authoring />} />} />
        <Route path="/zmiany" element={<Guarded path="/zmiany" element={<Changes />} />} />
        <Route path="/alerty" element={<Guarded path="/alerty" element={<Alerts />} />} />
        <Route path="/agenci" element={<Guarded path="/agenci" element={<Agents />} />} />
        {/**
          * KREATOR WDROŻENIA SMART SYNCA — te same trzy warunki co Agenci i Kopie, bo prowadzi
          * przez czynności, z których każda z osobna jest już tak bramkowana.
          */}
        <Route path="/smart-sync" element={<Guarded path="/smart-sync" element={<SmartSync />} />} />
        <Route path="/programy" element={<Guarded path="/programy" element={<Programy />} />} />
        <Route path="/kopie" element={<Guarded path="/kopie" element={<Kopie />} />} />
        <Route path="/logi" element={<Guarded path="/logi" element={<Logs />} />} />
        <Route path="/ustawienia" element={<Guarded path="/ustawienia" element={<Settings />} />} />
        <Route path="/konta" element={<Guarded path="/konta" element={<Accounts />} />} />
      </Route>
    </Routes>
  );
}
